import { parse, isValid } from "date-fns";
import { stateOptions } from "data";

export const normalizeCarrierArr = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .filter((s) => s !== null && s !== undefined && s !== "")
    .map((s) => String(s));

export const yn = (v) => (v ? "yes" : "no");
export const ynna = (v) => (v == null ? "na" : v ? "yes" : "no");
export const ynBool = (v) => v === true || v === "Y";

export const cleanPhone = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, "").slice(0, 10);

    // Format as (000) 000-0000
    if (digits.length < 4) return digits;
    if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

export const normalizeAddress = (a = {}) => ({
  address1: a.address1 ?? "",
  address2: a.address2 ?? "",
  city: a.city ?? "",
  state: stateOptions.find((s) => s.label === a.state || s.value === a.state)?.value ?? "",
  zip: a.zip ?? "",
});

const toAddressShape = (maybeAddress) => {
  if (maybeAddress && typeof maybeAddress === "object" && !Array.isArray(maybeAddress)) {
    const { address1, address2, city, state, zip } = maybeAddress;
    return { address1, address2, city, state, zip };
  }
  return {};
};

const normalizeCompanyAddress = (company = {}, type = "mailing") => {
  const base =
    type === "mailing"
      ? {
          address1: company.address,
          address2: company.address2,
          city: company.city,
          state: company.state,
          zip: company.zip,
        }
      : {
          address1: company.garaging_address,
          address2: company.garaging_address2,
          city: company.garaging_city,
          state: company.garaging_state,
          zip: company.garaging_zip,
        };

  const override =
    type === "mailing"
      ? toAddressShape(company.mailing_address)
      : toAddressShape(company.garaging_address);

  return normalizeAddress({ ...base, ...override });
};

export const normalizeCompanyMailingAddress = (company = {}) =>
  normalizeCompanyAddress(company, "mailing");

export const normalizeCompanyGaragingAddress = (company = {}) =>
  normalizeCompanyAddress(company, "garaging");

export function mapDriver(d) {
  const parseDateSafe = (dateStr) => {
    if (!dateStr) return null;

    const formats = ["MM/dd/yyyy", "yyyy-MM-dd"];
    for (const fmt of formats) {
      const parsed = parse(dateStr, fmt, new Date());
      if (isValid(parsed)) return parsed;
    }

    // If both formats fail, return null
    return null;
  };

  const toNumber = (value, fallback = 0) => {
    if (value === null || value === undefined || value === "") return fallback;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : fallback;
  };

  return {
    id: d.id,
    driverId: d.id,
    firstName: d.firstName ?? "",
    lastName: d.lastName ?? "",
    dateOfBirth: parseDateSafe(d.dateOfBirth),
    dateOfHire: parseDateSafe(d.dateOfHire),
    yearsExperience: toNumber(d.yearsExperience),
    licenseState:
      stateOptions.find(
        (s) => s.label === d.licenseState || s.value === d.licenseState
      )?.value ?? "",
    licenseNumber: d.licenseNumber ?? "",
    accidents: toNumber(d.accidents_total ?? d.accidents),
    violations: toNumber(d.violations_total ?? d.violations),
    suspensions: toNumber(d.suspensions_total ?? d.suspensions),
    eligibility: d.eligibility,
    exclude: !!d.exclude,
    status: d.status ?? "active",
  };
}

export function mapVehicle(v) {
  return {
    id: v.id,
    vehicleId: v.id,
    year: v.year,
    make: v.make,
    model: v.model,
    vin: v.vin,
    value: v.value,
    bodyType: v.bodyTypeKey,
    class: v.classKey,
    includeApdTowing: !!v.includeApdTowing,
    hitchType: v.hitchType,
  };
}

export function mapTrailer(t) {
  return {
    id: t.id,
    trailerId: t.id,
    type: "trailer",
    year: t.year,
    make: t.make,
    model: t.model,
    vin: t.vin,
    value: t.value,
    bodyType: t.bodyTypeKey,
    deductibleType: t.deductibleType,
    deductible: t.deductible,
  };
}

export function sumTrailerValues(trailers = []) {
  return trailers.reduce((sum, t) => sum + (Number(t.value) || 0), 0);
}

export function mapLoss(l) {
  return {
    autoLiability: { count: l.lossAlCount ?? 0, paid: l.lossAlPaid ?? 0 },
    physicalDamage: { count: l.lossApdCount ?? 0, paid: l.lossApdPaid ?? 0 },
    cargo: { count: l.lossMtcCount ?? 0, paid: l.lossMtcPaid ?? 0 },
    tgl: { count: l.lossTglCount ?? 0, paid: l.lossTglPaid ?? 0 },
  };
}

export function makeEmptyLosses() {
  return [
    mapLoss({}), // last 12 months
    mapLoss({}), // prior 12 months
    mapLoss({}), // prior 12 months again
  ];
}

export const toYN = (v) => {
  // Accept booleans, "yes"/"no", "Y"/"N", 1/0. Returns undefined when unknown.
  if (v === true || v === "yes" || v === "Y" || v === 1) return "Y";
  if (v === false || v === "no" || v === "N" || v === 0) return "N";
  return undefined;
};

export const fromYN_bool = (v, fallback = false) => {
  return v === "Y" ? true : v === "N" ? false : fallback;
};

export const fromYN_yesno = (
  v,
  fallback = "na"
) => {
  if (v === "Y") return "yes";
  if (v === "N") return "no";
  return fallback;
};

/** For schema fields that are set only when "checked", else undefined. */
export const fromYN_yesOnly = (v) =>
  v === "Y" ? "yes" : undefined;

export const nullIfUnselected = (v) =>
  (v) === "unselected" || v === "" ? null : (v);
